import Reflux from 'reflux';
import { Map, List, OrderedMap } from 'immutable';
import BaseMixin from 'flux/stores/BaseMixin';
import FormMixin from 'flux/stores/FormMixin';
import ExportFormActions from 'flux/actions/ExportFormActions';
import {
  FileTypes,
  LayoutSize,
  Orientation,
  PageSizes,
} from 'shared/constants/Export';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';
import { EditableCellTypes } from 'flux/stores/cuesheet/CuesheetConstants';
import { DisplayTypes } from 'shared/constants/FlowConstants';

/**
 * ExportFormStore
 */
export default Reflux.createStore({
  listenables: [ExportFormActions],

  mixins: [BaseMixin, FormMixin],

  init() {
    this._initForm();
    this.listenTo(CuesheetStore, 'onCuesheetStoreChange');
  },

  _initForm() {
    this.setState(
      Map({
        size: PageSizes.letter,
        type: FileTypes.xls,
        orientation: Orientation.landscape,
        layoutSize: LayoutSize.compact,
        firstPanelIsOpen: true,
        secondPanelIsOpen: true,
        columns: OrderedMap(),
        columnOrder: List(),
        disabledColumnOrder: List(),
        privateNotesPopulated: false,
        titleRowsEnabled: true,
      }),
    );
  },

  onClear() {
    this._initForm();
  },

  onSetCurrentlyOpenPanel(first, second) {
    const next = this.getState()
      .set('firstPanelIsOpen', first)
      .set('secondPanelIsOpen', second);

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onPopulate(teamId, eventId, show) {
    const next = this.getState().set('teamId', teamId).set('eventId', eventId);

    this.setStateAndTrigger(next);
  },

  onToggleColumn(columnId) {
    let next = this.getState().updateIn(['columns', columnId], (column) => {
      return column.set('enabled', !column.get('enabled'));
    });

    const activeIndex = next.get('columnOrder').indexOf(columnId);
    const disabledIndex = next.get('disabledColumnOrder').indexOf(columnId);

    if (activeIndex !== -1) {
      next = next.withMutations((st) => {
        st.update('columnOrder', (order) => {
          return order.splice(activeIndex, 1);
        });
        st.update('disabledColumnOrder', (order) => {
          return order.push(columnId);
        });
      });
    } else if (disabledIndex !== -1) {
      next = next.withMutations((st) => {
        st.update('disabledColumnOrder', (order) => {
          return order.splice(disabledIndex, 1);
        });
        st.update('columnOrder', (order) => {
          return order.push(columnId);
        });
      });
    }

    this.setStateAndTrigger(next);
  },

  onTogglePrivateNotesPopulation() {
    const next = this.getState().update(
      'privateNotesPopulated',
      (bool) => !bool,
    );

    this.setStateAndTrigger(next);
  },

  onToggleTitleRows() {
    const next = this.getState().update('titleRowsEnabled', (bool) => !bool);

    this.setStateAndTrigger(next);
  },

  onMoveColumnUp(columnId) {
    const state = this.getState();

    const existingIndex = state.get('columnOrder').indexOf(columnId);

    //0 represnts the top of the order so do nothing.
    if (existingIndex === 0) return;

    const afterColumnId = state.get('columnOrder').get(existingIndex - 1);

    this._moveColumnDown(afterColumnId);
  },

  onMoveColumnDown(columnId) {
    this._moveColumnDown(columnId);
  },

  _moveColumnDown(columnId) {
    let next = this.getState();

    const existingIndex = next.get('columnOrder').indexOf(columnId);
    const afterColumnId = next.get('columnOrder').get(existingIndex + 1);

    next = next.withMutations((st) => {
      st.update('columnOrder', (order) => {
        /**
         * First remove the existing column
         */
        const nextOrder = order.splice(existingIndex, 1);

        /**
         * Next get the index of the column we're moving the other column after
         */
        const afterColumnExistingIndex = nextOrder.indexOf(afterColumnId);

        /**
         * Finally add the column back into the order with the new position
         */
        return nextOrder.splice(afterColumnExistingIndex + 1, 0, columnId);
      });
    });

    this.setStateAndTrigger(next);
  },

  onCuesheetStoreChange(cuesheetState) {
    const cuesheetColumnOrder = cuesheetState.get('columnOrder');
    const cuesheetFullColumnOrder = cuesheetState.get('fullColumnOrder');
    const cuesheetFullColumns = cuesheetState.get('fullColumns');
    const show = cuesheetState.get('show');
    let next = this.getState();

    if (!cuesheetColumnOrder || !cuesheetFullColumns || !show) return;

    const specialColumns = this._getSpecialColumns(show);
    const dataColumns = this._getDataColumns(
      cuesheetFullColumnOrder,
      cuesheetFullColumns,
      cuesheetColumnOrder,
    );
    const allColumns = specialColumns.merge(dataColumns);

    if (
      show.get('_id') !== this.getInState(['show', '_id']) ||
      !allColumns.equals(next.get('columns'))
    ) {
      let columnOrder = List();
      let disabledColumnOrder = List();

      allColumns.forEach((column, columnId) => {
        if (column.get('enabled')) {
          columnOrder = columnOrder.push(columnId);
        } else {
          disabledColumnOrder = disabledColumnOrder.push(columnId);
        }
      });

      next = next
        .set('columnOrder', columnOrder)
        .set('disabledColumnOrder', disabledColumnOrder)
        .set('columns', allColumns);
    }

    next = next.set('show', show);

    this.setStateAndTrigger(next);
  },

  _getDataColumns(
    cuesheetFullColumnOrder,
    cuesheetFullColumns,
    cuesheetColumnOrder,
  ) {
    let columns = OrderedMap();

    cuesheetFullColumnOrder.forEach((columnId) => {
      let column = cuesheetFullColumns.get(columnId);

      if (this.getInState(['columns', columnId])) {
        column = this.getInState(['columns', columnId]).merge(column);
      }

      if (!cuesheetColumnOrder.includes(columnId)) {
        column = column.set('enabled', false);
      } else {
        column = column.set('enabled', true);
      }

      columns = columns.set(columnId, column);
    });

    return columns;
  },

  _getSpecialColumns(show) {
    return OrderedMap().withMutations((columns) => {
      if (show.get('displayItemNumbers')) {
        columns.set(
          EditableCellTypes.ItemNumber,
          Map({
            _id: EditableCellTypes.ItemNumber,
            title: 'Item Numbers',
            enabled: true,
          }),
        );
      } else if (show.get('displayType') === DisplayTypes.show) {
        columns.set(
          EditableCellTypes.ItemNumber,
          Map({
            _id: EditableCellTypes.ItemNumber,
            title: 'Item Numbers',
            enabled: true,
          }),
        );
      }

      if (show.get('displayItemTitles')) {
        columns.set(
          EditableCellTypes.ItemTitle,
          Map({
            _id: EditableCellTypes.ItemTitle,
            title: 'Items',
            enabled: true,
          }),
        );
      }

      if (show.get('displayItemDescriptions')) {
        columns.set(
          EditableCellTypes.ItemDescription,
          Map({
            _id: EditableCellTypes.ItemDescription,
            title: 'Item Descriptions',
            enabled: true,
          }),
        );
      }

      if (show.get('displayStartTime')) {
        columns.set(
          EditableCellTypes.ItemStartTime,
          Map({
            _id: EditableCellTypes.ItemStartTime,
            title: 'Start Time',
            enabled: true,
          }),
        );
      }

      if (show.get('displayPresetDuration')) {
        columns.set(
          EditableCellTypes.ItemPresetDuration,
          Map({
            _id: EditableCellTypes.ItemPresetDuration,
            title: 'Preset Time',
            enabled: true,
          }),
        );
      }

      if (show.get('displayDuration')) {
        columns.set(
          EditableCellTypes.ItemDuration,
          Map({
            _id: EditableCellTypes.ItemDuration,
            title: 'Duration',
            enabled: true,
          }),
        );
      }

      columns.set(
        EditableCellTypes.ItemNote,
        Map({
          _id: EditableCellTypes.ItemNote,
          title: 'Private Notes',
          enabled: true,
        }),
      );
    });
  },
});
